// Canonical JSON: a strict subset of RFC 8785 (JCS). See docs/spec/events.md §1.
import { sha256Hex, utf8 } from "./bytes.ts";

export class CanonicalError extends Error {
  path: string;
  constructor(path: string, message: string) {
    super(`${path || "$"}: ${message}`);
    this.path = path;
  }
}

function ser(v: unknown, path: string): string {
  if (v === null) return "null";
  switch (typeof v) {
    case "boolean":
      return v ? "true" : "false";
    case "number":
      if (!Number.isSafeInteger(v)) throw new CanonicalError(path, `number ${v} is not a safe integer`);
      return Object.is(v, -0) ? "0" : String(v);
    case "string":
      if (v.normalize("NFC") !== v) throw new CanonicalError(path, "string is not NFC");
      return JSON.stringify(v);
    case "object": {
      if (Array.isArray(v)) return `[${v.map((x, i) => ser(x, `${path}[${i}]`)).join(",")}]`;
      const proto = Object.getPrototypeOf(v);
      if (proto !== Object.prototype && proto !== null) throw new CanonicalError(path, "not a plain object");
      // Default sort compares UTF-16 code units, which is exactly what JCS requires.
      const keys = Object.keys(v as object).sort();
      return `{${keys
        .map((k) => {
          if (k.normalize("NFC") !== k) throw new CanonicalError(`${path}.${k}`, "key is not NFC");
          return `${JSON.stringify(k)}:${ser((v as Record<string, unknown>)[k], `${path}.${k}`)}`;
        })
        .join(",")}}`;
    }
    default:
      throw new CanonicalError(path, `${typeof v} is not allowed`);
  }
}

export const canonicalize = (v: unknown): string => ser(v, "");
export const canonicalBytes = (v: unknown): Uint8Array => utf8(canonicalize(v));
export const canonicalHash = (v: unknown): Promise<string> => sha256Hex(canonicalBytes(v));
